import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/lib/api-client";

export interface AnnouncementAdminResponse {
  id: string;
  title: string;
  body_md: string;
  cta_label: string | null;
  cta_url: string | null;
  target_tags: string[];
  all_users: boolean;
  email_requested: boolean;
  email_sent_at: string | null;
  created_by_user_id: string | null;
  created_at: string;
  updated_at: string;
  ack_count: number;
  target_user_count: number;
}

export interface AnnouncementPublishResponse extends AnnouncementAdminResponse {
  email_status:
    | "scheduled"
    | "skipped_resend_not_configured"
    | "not_requested";
}

export interface AnnouncementCreateBody {
  title: string;
  body_md: string;
  cta_label?: string | null;
  cta_url?: string | null;
  target_tags: string[];
  all_users: boolean;
  email_requested: boolean;
}

export type AnnouncementUpdateBody = Partial<
  Omit<AnnouncementCreateBody, "email_requested">
>;

export interface AudiencePreview {
  target_user_count: number;
  email_eligible_count: number;
}

export function useAdminAnnouncements() {
  return useQuery<AnnouncementAdminResponse[]>({
    queryKey: ["admin", "announcements", "list"],
    queryFn: () => apiClient.get("api/admin/announcements").json(),
    staleTime: 30_000,
  });
}

export function useCreateAnnouncement() {
  const qc = useQueryClient();
  return useMutation<
    AnnouncementPublishResponse,
    Error,
    AnnouncementCreateBody
  >({
    mutationFn: (body) =>
      apiClient.post("api/admin/announcements", { json: body }).json(),
    onSettled: () =>
      qc.invalidateQueries({ queryKey: ["admin", "announcements"] }),
  });
}

export function useUpdateAnnouncement() {
  const qc = useQueryClient();
  return useMutation<
    AnnouncementAdminResponse,
    Error,
    { id: string; body: AnnouncementUpdateBody }
  >({
    mutationFn: ({ id, body }) =>
      apiClient
        .patch(`api/admin/announcements/${id}`, { json: body })
        .json(),
    onSettled: () =>
      qc.invalidateQueries({ queryKey: ["admin", "announcements"] }),
  });
}

export function useDeleteAnnouncement() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) =>
      apiClient
        .delete(`api/admin/announcements/${id}`)
        .then(() => undefined),
    onSettled: () =>
      qc.invalidateQueries({ queryKey: ["admin", "announcements"] }),
  });
}

export function useAudiencePreview(
  target_tags: string[],
  all_users: boolean,
  enabled = true,
) {
  return useQuery<AudiencePreview>({
    queryKey: [
      "admin",
      "announcements",
      "audience",
      { target_tags: [...target_tags].sort(), all_users },
    ],
    queryFn: () => {
      const params = new URLSearchParams();
      params.set("all_users", String(all_users));
      for (const t of target_tags) params.append("target_tags", t);
      return apiClient
        .get(`api/admin/announcements/audience-preview?${params}`)
        .json();
    },
    enabled,
    staleTime: 10_000,
  });
}
